<script setup>
import { ref, computed } from "vue";
import HanaRibbonBackdrop from "@/components/new/HanaRibbonBackdrop.vue";

const props = defineProps({
  // bible: 하나 바이블 부서 게시판, ribbon: 하나 리본 게시판 (사역 리본, 행사 안내)
  type: {
    type: String,
    default: "bible",
  },
});

// 게시판 묶음별 배너 문구
const banners = {
  bible: {
    title: "하나 바이블 사역 나눔터",
    subtitle: ["하나 바이블로 물든 우리 교회 이야기,", "함께 나누고, 함께 성장해요!"],
    hashtags: ["사역꿀팁", "현장노하우", "나만의자료"],
  },
  ribbon: {
    title: "하나 리본",
    subtitle: ["함께 나누는 마음이", "교회교육을 더 풍성하게 합니다."],
    hashtags: ["나눔", "채움", "연결", "세움"],
  },
};
const banner = computed(() => banners[props.type] ?? banners.bible);
const isBible = computed(() => props.type !== "ribbon");

// 공유 추천 자료 펼침 여부
const isGuideOpen = ref(false);

const toggleGuide = () => {
  isGuideOpen.value = !isGuideOpen.value;
};

// 공유하면 좋은 자료 (아이콘: heroicons outline path)
const shareGuides = [
  {
    text: "아이들이 좋아하는 공과 활용 꿀팁",
    icon: [
      "M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18",
    ],
  },
  {
    text: "하나 바이블 활용 특별 이벤트",
    icon: [
      "M21 11.25v8.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 1 0 9.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1 1 14.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z",
    ],
  },
  {
    text: "사역 현장 이모저모",
    icon: [
      "M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z",
      "M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z",
    ],
  },
  {
    text: "교사교육 및 부서 운영 팁 및 자료",
    icon: [
      "M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5",
    ],
  },
];
</script>

<template>
  <div class="board-hero">
    <!-- 배너 -->
    <div class="banner">
      <template v-if="isBible">
        <img
          class="banner-image"
          :src="require('@/assets/img/community_banner.jpg')"
          alt=""
        />
        <div class="banner-overlay" aria-hidden="true"></div>
      </template>
      <!-- 하나 리본 소개 페이지와 같은 배경 -->
      <HanaRibbonBackdrop v-else class="-z-20" />

      <!-- 아래 안내 카드가 배너 하단을 덮는 만큼 내용을 위로 올린다 -->
      <div class="banner-content" :class="{ 'pb-6 md:pb-8': isBible }">
        <h1 class="banner-title">{{ banner.title }}</h1>
        <p class="banner-subtitle">
          {{ banner.subtitle[0] }}<br class="sm:hidden" />
          {{ banner.subtitle[1] }}
        </p>
        <ul class="mt-5 flex flex-wrap gap-2">
          <li v-for="tag in banner.hashtags" :key="tag" class="banner-tag">
            #{{ tag }}
          </li>
        </ul>
      </div>
    </div>

    <!-- 우수 참여교회 안내 -->
    <div v-if="isBible" class="notice-card">
      <div class="notice-body">
        <div class="flex items-start gap-4 grow basis-0 min-w-0">
          <span class="notice-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.6"
              stroke="currentColor"
              class="w-6 h-6"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 0 0 2.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 0 1 2.916.52 6.003 6.003 0 0 1-5.395 4.972m0 0a6.726 6.726 0 0 1-2.749 1.35m0 0a6.772 6.772 0 0 1-3.044 0"
              />
            </svg>
          </span>
          <div class="min-w-0">
            <p class="notice-title">
              매달 <strong class="text-blue-600">우수 참여교회</strong>를
              선정하여 특별 혜택과 사역 소개 기회를 드립니다!
            </p>
            <p class="notice-desc">
              지금 바로 나누고, 하나님 나라를 위한 동역에 참여하세요!
            </p>
          </div>
        </div>

        <button
          type="button"
          class="guide-toggle"
          :aria-expanded="isGuideOpen"
          aria-controls="share-guide"
          @click="toggleGuide"
        >
          어떤 자료를 공유하면 좋을까요?
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            class="w-4 h-4 transition-transform duration-300"
            :class="{ 'rotate-180': isGuideOpen }"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="m19.5 8.25-7.5 7.5-7.5-7.5"
            />
          </svg>
        </button>
      </div>

      <!-- 공유 추천 자료 -->
      <div
        id="share-guide"
        class="guide-panel"
        :class="isGuideOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
        :aria-hidden="!isGuideOpen"
      >
        <div class="overflow-hidden">
          <ul class="guide-list">
            <li v-for="guide in shareGuides" :key="guide.text" class="guide-item">
              <span class="guide-item-icon" aria-hidden="true">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.6"
                  stroke="currentColor"
                  class="w-5 h-5"
                >
                  <path
                    v-for="d in guide.icon"
                    :key="d"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    :d="d"
                  />
                </svg>
              </span>
              <span>{{ guide.text }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.board-hero {
  @apply w-full max-w-[82.75rem] mx-auto px-4 sm:px-6 lg:px-8 pt-4 lg:pt-6;
}

/* 배너 */
.banner {
  @apply relative isolate overflow-hidden rounded-2xl lg:rounded-3xl h-[15rem] md:h-[18rem];
}

.banner-image {
  @apply absolute inset-0 -z-20 w-full h-full object-cover;
}

.banner-overlay {
  @apply absolute inset-0 -z-10 bg-gradient-to-r from-slate-950/80 via-slate-900/50 to-slate-900/10;
}

.banner-content {
  @apply flex h-full flex-col justify-center px-6 sm:px-10 lg:px-14 text-white;
}

.banner-title {
  @apply text-[1.75rem] md:text-[2.5rem] font-extrabold leading-tight tracking-tight;
}

.banner-subtitle {
  @apply mt-2 md:mt-3 text-[0.9375rem] md:text-lg font-medium leading-relaxed text-white/90;
}

.banner-tag {
  @apply rounded-full bg-white/15 px-3 py-1 text-xs md:text-sm font-medium text-white ring-1 ring-inset ring-white/30 backdrop-blur-sm;
}

/* 우수 참여교회 안내 */
.notice-card {
  @apply relative mx-2 sm:mx-6 lg:mx-10 -mt-8 md:-mt-10 rounded-2xl border border-gray-100 bg-white shadow-[0_12px_32px_-12px_rgba(15,23,42,0.18)];
}

.notice-body {
  @apply flex flex-col md:flex-row md:items-center gap-4 md:gap-6 p-5 md:px-7 md:py-6;
}

.notice-icon {
  @apply flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-500;
}

.notice-title {
  @apply text-[0.9375rem] md:text-base font-semibold leading-snug text-gray-900 break-keep;
}

.notice-desc {
  @apply mt-1 text-sm text-gray-500 break-keep;
}

.guide-toggle {
  @apply inline-flex shrink-0 items-center justify-center gap-1.5 h-10 px-4 rounded-full bg-blue-50 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2;
}

/* 공유 추천 자료 */
.guide-panel {
  @apply grid transition-[grid-template-rows] duration-300 ease-out;
}

.guide-list {
  @apply grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3 border-t border-gray-100 p-5 md:px-7 md:pb-6;
}

.guide-item {
  @apply flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium text-gray-700 break-keep;
}

.guide-item-icon {
  @apply flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-blue-600 shadow-sm;
}
</style>
